import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { ExpandMore, ChevronRight, Add, Edit, DeleteOutline } from '@mui/icons-material';

/**
 * O menu de seções da central — a árvore da esquerda.
 *
 * Espelha a navegação da central que a SysFar mantém no Movidesk: seções na
 * raiz, subseções dentro, e a contagem de artigos de cada ramo.
 *
 * ## A conta inclui as subseções
 *
 * Lá, "Fiscal" mostra 59 — o que está nele e o que está abaixo. Uma seção que
 * só agrupa outras apareceria com zero, e pareceria vazia. Quem soma é o
 * servidor; aqui só se desenha.
 *
 * ## "Sem categoria" não é um detalhe
 *
 * São 265 dos 275 documentos de hoje. Enquanto ninguém os encaixar na árvore,
 * é por ali que se chega neles — e esconder isso faria a central parecer vazia
 * tendo a base inteira dentro.
 */

const Ramo = ({
    secao,
    nivel,
    selecionada,
    aoSelecionar,
    podeEditar,
    aoCriarFilha,
    aoRenomear,
    aoApagar,
}) => {
    const [aberto, setAberto] = useState(nivel === 0);
    const temFilhos = (secao.children || []).length > 0;
    const estaSelecionada = selecionada === secao.id;

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    pl: nivel * 1.5,
                    py: 0.35,
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: estaSelecionada ? 'action.selected' : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&:hover .acoes-da-secao': { opacity: 1 },
                }}
                data-testid={`secao-${secao.id}`}
                onClick={() => aoSelecionar(secao.id)}
            >
                {/*
                  O chevron só aparece quando há o que abrir. Um chevron que não
                  faz nada ensina a pessoa a não clicar nele.
                */}
                {temFilhos ? (
                    <IconButton
                        size="small"
                        sx={{ p: 0.1 }}
                        aria-label={aberto ? `Recolher ${secao.name}` : `Expandir ${secao.name}`}
                        data-testid={`expandir-${secao.id}`}
                        onClick={(e) => { e.stopPropagation(); setAberto((a) => !a); }}
                    >
                        {aberto ? <ExpandMore sx={{ fontSize: 16 }} /> : <ChevronRight sx={{ fontSize: 16 }} />}
                    </IconButton>
                ) : (
                    <Box sx={{ width: 20, flexShrink: 0 }} />
                )}

                <Typography
                    sx={{
                        flexGrow: 1,
                        fontSize: '13px',
                        fontWeight: estaSelecionada ? 600 : 400,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {secao.name}
                </Typography>

                <Typography sx={{ fontSize: '11px', color: 'text.secondary', pr: 0.5 }}>
                    {secao.artigos}
                </Typography>

                {podeEditar && (
                    <Box
                        className="acoes-da-secao"
                        sx={{ display: 'flex', opacity: 0, transition: 'opacity .15s' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Tooltip title="Nova subseção">
                            <IconButton size="small" sx={{ p: 0.2 }} data-testid={`nova-filha-${secao.id}`}
                                onClick={() => aoCriarFilha(secao)}>
                                <Add sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Renomear">
                            <IconButton size="small" sx={{ p: 0.2 }} data-testid={`renomear-${secao.id}`}
                                onClick={() => aoRenomear(secao)}>
                                <Edit sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Apagar seção">
                            <IconButton size="small" sx={{ p: 0.2 }} data-testid={`apagar-${secao.id}`}
                                onClick={() => aoApagar(secao)}>
                                <DeleteOutline sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {aberto && temFilhos && (secao.children || []).map((filha) => (
                <Ramo
                    key={filha.id}
                    secao={filha}
                    nivel={nivel + 1}
                    selecionada={selecionada}
                    aoSelecionar={aoSelecionar}
                    podeEditar={podeEditar}
                    aoCriarFilha={aoCriarFilha}
                    aoRenomear={aoRenomear}
                    aoApagar={aoApagar}
                />
            ))}
        </Box>
    );
};

const ArvoreDeSecoes = ({
    secoes,
    semCategoria,
    selecionada,
    aoSelecionar,
    podeEditar = false,
    aoCriarFilha = () => {},
    aoRenomear = () => {},
    aoApagar = () => {},
}) => (
    <Box data-testid="arvore-de-secoes">
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                py: 0.35,
                px: 0.5,
                borderRadius: 1,
                cursor: 'pointer',
                backgroundColor: selecionada === 'todas' ? 'action.selected' : 'transparent',
                '&:hover': { backgroundColor: 'action.hover' },
            }}
            data-testid="secao-todas"
            onClick={() => aoSelecionar('todas')}
        >
            <Typography sx={{ fontSize: '13px', fontWeight: selecionada === 'todas' ? 600 : 400 }}>
                Todos os artigos
            </Typography>
        </Box>

        {(secoes || []).map((secao) => (
            <Ramo
                key={secao.id}
                secao={secao}
                nivel={0}
                selecionada={selecionada}
                aoSelecionar={aoSelecionar}
                podeEditar={podeEditar}
                aoCriarFilha={aoCriarFilha}
                aoRenomear={aoRenomear}
                aoApagar={aoApagar}
            />
        ))}

        {/*
          Só aparece quando há o que mostrar. Hoje são 265 artigos; no dia em
          que a árvore estiver organizada, some sozinho.
        */}
        {semCategoria > 0 && (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 0.5,
                    pt: 0.5,
                    px: 0.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    backgroundColor: selecionada === 'sem-categoria' ? 'action.selected' : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
                data-testid="secao-sem-categoria"
                onClick={() => aoSelecionar('sem-categoria')}
            >
                <Typography sx={{ flexGrow: 1, fontSize: '13px', fontStyle: 'italic', color: 'text.secondary' }}>
                    Sem seção
                </Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>{semCategoria}</Typography>
            </Box>
        )}
    </Box>
);

export default ArvoreDeSecoes;
